import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2019 semantics GmbH
// Date        : $Date: 2026-06-24 22:57:08 +0200 (Wed, 24 Jun 2026) $
// Author      : Dirk Rothe, Tobias Schwarzkopf
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 163984 $
// Purpose     : header components
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Icon, Button, Header, Segment } from 'semantic-ui-react';
// ---Framework imports
import { isObject, isArray, isString, isNil, mapItems, dict, isBoolean } from 'ldash';
import log from 'uix/logging';
import { useState, useSelector, useUserSetting, useFullscreen, FullscreenContext, useRef, useEffect, usePartContext, useActions } from 'uix/hooks';
import { Sel } from 'uix/registries';
import { ucFirst } from 'uix/utils';
// ---Local imports
import { PortalTarget } from './portals';
import S from './header.css';
import { VIcon } from 'uix/graphics';
import { XMenu } from './menu';
import { Toolbar } from './toolbar';
import { XPopup } from './popup';
import { DebugFeature } from './debug';
import clsx from 'clsx';
const ToolbarWrap = ({ toolbar, children })=>{
    let { titled, size, colored } = toolbar;
    return toolbar ? /*#__PURE__*/ _jsx(Toolbar, {
        className: S.toolbar,
        titled: titled,
        size: size ? size : 'small',
        colored: colored && colored,
        children: children
    }) : /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
};
// ---Exports
export const HeaderSettingsIcon = ({ header, subheader = null, icon = null, menu = null, bottomMenu = null, portal = null, fluidMenu = false, style = {
    display: 'flex',
    justifyContent: 'space-between',
    verticalAlign: 'top'
}, basic = false, onIconClick = null, children, fullscreenable = false, isFullscreen, toggleFullscreen, loading = false, toolbar = true, compact = false, iconPopup })=>{
    let { tabname } = useSelector(Sel.request);
    header = header || ucFirst(tabname);
    let i18n = useSelector(Sel.i18n);
    let [iconPopupOpen, setIconPopupOpen] = useState(false);
    if (iconPopup) {
        onIconClick = ()=>setIconPopupOpen(!iconPopupOpen);
    }
    let iconProps = {};
    if (onIconClick) {
        iconProps = {
            style: {
                cursor: 'pointer'
            },
            onClick: onIconClick
        };
    }
    if (basic) {
        icon = null;
    }
    if (loading) {
        if (isString(icon)) {
            icon = [
                icon
            ];
        }
        if (isArray(icon)) {
            icon = [
                ...icon,
                {
                    name: 'sync',
                    loading: true,
                    color: 'green'
                }
            ];
        }
    }
    if (isArray(icon)) {
        let [icon1, icon2, icon3] = icon;
        let decode = (i)=>isString(i) ? {
                name: i
            } : {
                ...i
            };
        icon = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(VIcon, {
                    ...decode(icon1),
                    ...iconProps
                }),
                icon2 && /*#__PURE__*/ _jsx(VIcon, {
                    corner: "bottom right",
                    ...decode(icon2)
                }),
                icon3 && /*#__PURE__*/ _jsx(VIcon, {
                    corner: "bottom left",
                    ...decode(icon3)
                })
            ]
        });
    } else if (isObject(icon)) {
        iconProps = {
            ...icon,
            ...iconProps
        };
        icon = /*#__PURE__*/ _jsx(Icon, {
            ...iconProps
        });
    } else if (isString(icon)) {
        icon = /*#__PURE__*/ _jsx(VIcon, {
            name: icon,
            ...iconProps
        });
    } else if (!isNil(icon)) {
        log.warn('invalid icon format %o', icon);
    }
    let iconContent = /*#__PURE__*/ _jsx("span", {
        className: clsx(S.icon, compact && S.compact, iconPopupOpen && S.iconPopupOpen),
        children: /*#__PURE__*/ _jsx(Icon.Group, {
            children: icon
        })
    });
    if (iconPopup) {
        iconContent = /*#__PURE__*/ _jsx(XPopup, {
            open: iconPopupOpen,
            basic: true,
            fitted: true,
            hoverable: true,
            trigger: iconContent,
            onClose: ()=>setIconPopupOpen(false),
            children: iconPopup
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Header, {
                style: style,
                className: clsx(S.header, compact && S.compact),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        style: {
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center'
                        },
                        children: [
                            children && /*#__PURE__*/ _jsx("span", {
                                className: S.content,
                                children: children
                            }),
                            !basic ? iconContent : null,
                            !basic ? /*#__PURE__*/ _jsxs(Header.Content, {
                                children: [
                                    header,
                                    subheader && /*#__PURE__*/ _jsx(Header.Subheader, {
                                        children: subheader
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsxs(Header.Content, {
                                as: "h2",
                                children: [
                                    header,
                                    subheader && /*#__PURE__*/ _jsx(Header.Subheader, {
                                        children: subheader
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: clsx(S.menu, compact && S.compact, fluidMenu && S.fluidMenu),
                        style: {
                            display: 'flex'
                        },
                        children: [
                            /*#__PURE__*/ _jsxs(ToolbarWrap, {
                                toolbar: toolbar,
                                children: [
                                    menu,
                                    portal && /*#__PURE__*/ _jsx(PortalTarget, {
                                        id: portal
                                    })
                                ]
                            }),
                            fullscreenable && /*#__PURE__*/ _jsx(Button, {
                                secondary: true,
                                title: isFullscreen ? i18n.get('fullscreenClose') : i18n.get('fullscreenOpen'),
                                onClick: ()=>toggleFullscreen(),
                                style: {
                                    position: 'absolute',
                                    right: '0',
                                    top: '0'
                                },
                                size: "mini",
                                icon: isFullscreen ? 'close' : 'expand'
                            })
                        ]
                    })
                ]
            }),
            bottomMenu
        ]
    });
};
const Fullscreen = ({ isFullscreen, fRef, children, ...props })=>/*#__PURE__*/ _jsx("div", {
        ref: fRef,
        ...props,
        children: /*#__PURE__*/ _jsxs(FullscreenContext.Provider, {
            value: {
                isFullscreen,
                ref: fRef
            },
            children: [
                isFullscreen && /*#__PURE__*/ _jsx(PortalTarget, {
                    id: "fullscreenRootPortal"
                }),
                children
            ]
        })
    });
export const HeaderIconPartMenu = ({ toggleFullscreen })=>{
    let { name: partName, type: partType } = usePartContext();
    let clearPartPersistence = useActions('clearPartPersistence');
    let persistedUi = useSelector((root)=>root.ui.byId);
    // loop over all persisted query params and use stuff that has been modified
    let query = dict(mapItems(persistedUi, (name, data)=>{
        let pname = name.split('/').at(-1);
        let { value, initialValue } = data;
        if (isBoolean(value) && initialValue === '') {
            // toggle bools are initial ''
            initialValue = false;
        }
        if (String(value) != String(initialValue)) {
            return [
                pname,
                value
            ];
        }
    }, (name)=>name.startsWith(`Part/${partName}/query/`)).filter(Boolean));
    return /*#__PURE__*/ _jsxs(XMenu, {
        vertical: true,
        children: [
            /*#__PURE__*/ _jsx(XMenu.Label, {
                content: partName
            }),
            /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: "expand",
                content: "Fullscreen",
                onClick: toggleFullscreen
            }),
            /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: "configure",
                content: "Clear",
                title: "Clear local configuration",
                onClick: ()=>clearPartPersistence(partName)
            }),
            /*#__PURE__*/ _jsx(XMenu.LinkItem, {
                query: query,
                icon: "world",
                title: "Build persistent link for local configuration",
                content: "Link"
            }),
            /*#__PURE__*/ _jsx(DebugFeature, {
                children: /*#__PURE__*/ _jsx(XMenu.LinkItem, {
                    icon: "link",
                    content: "Part-Testing",
                    classname: "test",
                    tabname: "parts",
                    ctxid: partType
                })
            })
        ]
    });
};
export const HeaderSegment = ({ children, className, flex, height, overflow, overflowInner = false, overflowKey, flexInner = false, embedded = false, major, attached, compact, secondary, fullscreenable = true, headerIconPartMenu = true, headerless = false, fitted = false, style = {}, externalRef, ...props })=>{
    const { ref, toggleFullscreen, isFullscreen } = useFullscreen(externalRef);
    let [headerStyle, setHeaderStyle] = useUserSetting('headerStyle');
    // flex == true has two effects:
    // 1) grow this segment
    // 2) set flex-mode to column for children
    // 3) set overflow to hidden (children need to manage overflow now)
    if (flex) {
        style = {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            ...style
        };
    }
    if (height) {
        style = {
            ...style,
            height: `${height}px`
        };
    }
    let decode_ov = (ov)=>isString(ov) ? ov : 'auto';
    if (overflow) {
        style = {
            ...style,
            overflow: decode_ov(overflow)
        };
    }
    let header = null;
    if (!headerless) {
        if (headerStyle === 'compact') {
            props = {
                ...props,
                compact: true
            };
        }
        if (fullscreenable && !headerIconPartMenu) {
            props = {
                ...props,
                fullscreenable,
                isFullscreen,
                toggleFullscreen
            };
        }
        let iconPopup = headerIconPartMenu ? /*#__PURE__*/ _jsx(HeaderIconPartMenu, {
            toggleFullscreen: toggleFullscreen
        }) : null;
        header = /*#__PURE__*/ _jsx(HeaderSettingsIcon, {
            iconPopup: iconPopup,
            ...props
        });
    }
    let raised = true;
    if (major) {
        raised = false;
        className = clsx(className, S.majorSegment);
    }
    let overflowRef = useRef();
    useEffect(()=>{
        if (overflowKey && overflowRef.current) {
            overflowRef.current.scrollTop = 0;
        //~ overflowRef.current.scrollIntoView({behavior: 'smooth'})
        }
    }, [
        overflowKey
    ]);
    if (overflowInner) {
        let f = flexInner ? {
            flex: 1,
            display: 'flex',
            flexDirection: isString(flexInner) ? flexInner : 'column'
        } : {};
        children = /*#__PURE__*/ _jsx("div", {
            ref: overflowRef,
            style: {
                overflow: decode_ov(overflowInner),
                ...f
            },
            children: children
        });
    }
    let seg_props = {};
    if (fullscreenable) {
        seg_props = {
            ...seg_props,
            as: Fullscreen,
            fRef: ref,
            isFullscreen
        };
    }
    if (fitted) {
        style = {
            ...style,
            padding: 0
        };
    }
    if (embedded) {
        style = {
            ...style,
            height: '100%',
            border: '0',
            borderRadius: '0',
            boxShadow: 'none'
        };
    }
    return /*#__PURE__*/ _jsxs(Segment, {
        raised: raised,
        attached: attached,
        compact: compact,
        secondary: secondary,
        className: className,
        style: style,
        ...seg_props,
        children: [
            header,
            children
        ]
    });
};
export const TableSegment = ({ children, ...props })=>/* configure a HeaderSegment to play nice with a <SelectableTable flex/>
  flexInner="row" will position the inner table scrollbars correctly
   */ /*#__PURE__*/ _jsx(HeaderSegment, {
        major: true,
        flex: true,
        overflowInner: true,
        flexInner: "row",
        ...props,
        children: children
    });
